/**
 * Reconciliation: turning what this device captured offline into real orders.
 *
 * Runs when the failover monitor sees the authoritative server come back. For
 * each aggregate holding un-acknowledged events it posts the whole local log to
 * POST /api/mesh/reconcile, which replays it through the same order_service
 * commands an online captain uses and answers with the authoritative order id
 * plus a disposition for every event.
 *
 * Three rules this file exists to keep:
 *
 *   1. **The server decides what synced.** An event becomes `server_acked` only
 *      because it appeared in `acknowledged_event_ids`. The previous version
 *      POSTed each event to a mock endpoint that wrote it to a log table and
 *      answered "ok", and the UI believed it — so a captain's order looked
 *      synced while no order, KOT, bill or report line existed anywhere.
 *   2. **Sending twice is safe.** The whole log goes every time; the backend is
 *      idempotent on event_id. No partial-flush cursor to get wrong.
 *   3. **A conflict is news, not an error.** If the table was taken while this
 *      device was offline, the captain has to be told — retrying cannot fix it
 *      and silently attaching to the other party's order would merge two bills.
 */

import { networkState, NetworkMode } from "../networkState";
import { failoverMonitor } from "./failoverMonitor";
import {
  loadUnsyncedAggregateIds,
  loadAggregateForSync,
  markEventsAcknowledged,
  markAggregateRejected,
  rematerializeAggregate,
  saveOrderMapping,
  reviveRejectedAggregate,
  markAggregateDiscarded,
} from "../db";
import { offlineDb } from "@/db/offlineDb";

/** Captures older than this are dropped at flush time, never reconciled. */
export { STALE_CAPTURE_MAX_AGE_MS } from "../domain/staleness";
import { STALE_CAPTURE_MAX_AGE_MS } from "../domain/staleness";

/** How often a connected pad re-sends what the server has not confirmed. Was
 * 15 s: a send that failed once (a Wi-Fi blip mid-request) waited that long
 * before its next try. It only sends when something is unconfirmed. */
export const SWEEP_INTERVAL_MS = 3000;
/** Longest an unconfirmed aggregate waits between background re-sends. */
export const SWEEP_BACKOFF_MAX_MS = 60000;

/** Refused captures newer than this are re-sent automatically once per start. */
export const REFUSED_RETRY_MAX_AGE_MS = 60 * 60 * 1000;

class SyncService {
  constructor() {
    this.initialized = false;
    this.running = false;
    /** Aggregates the server refused, for the UI to surface. */
    this.conflicts = [];
    this.onConflict = null;
    /** aggregate_id -> { running, queued }: per-aggregate flush serialization. */
    this._flushes = new Map();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    failoverMonitor.onRecoveryRequested = () => this.reconcileWithServer();
    // Lost-ack self-heal. A flush whose RESPONSE never arrived (tunnel blip,
    // tab backgrounded mid-request) leaves events local while the server has
    // already applied them — the screen then says "waiting for server" forever
    // beside a KOT the kitchen is already cooking, because nothing re-flushed
    // until the next punch or an offline→online transition. While connected,
    // sweep for unsynced aggregates and re-flush; the server is idempotent on
    // event_id and flushes are serialized per aggregate, so a sweep that races
    // a real punch coalesces instead of double-sending.
    this._retryTimer = setInterval(async () => {
      if (this.running) return;
      if (networkState.getMode() !== NetworkMode.SERVER_CONNECTED) return;
      if (!this._refusedRetried) {
        this._refusedRetried = true;
        await this.retryRefusedCaptures().catch(() => {});
      }
      try {
        await this.sweepUnconfirmed();
      } catch { /* swept again next tick */ }
    }, SWEEP_INTERVAL_MS);
  }

  /**
   * The background re-send: each unconfirmed aggregate on its own clock.
   *
   * Not reconcileWithServer: that flips the mode to RECONCILING (a punch made
   * meanwhile skips its immediate send) and fails over to the mesh when every
   * aggregate errors, so a 3 s sweep would pulse the indicator and could loop
   * the pad between offline and online with the server healthy. An aggregate
   * the server answered but held back (a deferred KOT) or one whose send
   * failed waits longer each time, up to SWEEP_BACKOFF_MAX_MS, instead of
   * POSTing its whole log every 3 s for the rest of the night.
   */
  async sweepUnconfirmed() {
    const ids = await loadUnsyncedAggregateIds();
    const now = Date.now();
    this._sweepWait = this._sweepWait || new Map();
    for (const id of [...this._sweepWait.keys()]) {
      if (!ids.includes(id)) this._sweepWait.delete(id);
    }
    for (const id of ids) {
      const wait = this._sweepWait.get(id);
      if (wait && wait.at > now) continue;
      const backOff = () => {
        const ms = Math.min((wait?.ms || SWEEP_INTERVAL_MS) * 2, SWEEP_BACKOFF_MAX_MS);
        this._sweepWait.set(id, { ms, at: Date.now() + ms });
      };
      try {
        const result = await this.reconcileAggregate(id);
        if (result && !result.conflict && !result.deferred?.length && !result.skipped?.length) {
          this._sweepWait.delete(id);
        } else {
          backOff();
        }
      } catch {
        backOff();
      }
    }
  }

  /**
   * The reload button's sync, on demand: every unconfirmed aggregate now,
   * whatever its back-off, plus the captures the server refused before.
   * Returns counts the button reports in words: what went through, what the
   * server held back (with its reasons), and what could not be sent.
   */
  async forceSync() {
    this._sweepWait = new Map();
    await this.retryRefusedCaptures().catch(() => {});
    const ids = await loadUnsyncedAggregateIds();
    const summary = { total: ids.length, sent: 0, held: 0, failed: 0, dropped: 0, reasons: [] };
    for (const id of ids) {
      try {
        const result = await this.reconcileAggregate(id);
        const held = [...(result?.deferred || []), ...(result?.skipped || [])];
        if (result === null) {
          // _reconcileNow sends nothing for an empty log or a capture older
          // than STALE_CAPTURE_MAX_AGE_MS (quarantined): never "sent".
          summary.dropped += 1;
        } else if (result?.conflict) {
          summary.held += 1;
          summary.reasons.push(result.conflict.detail?.message || result.conflict.code || "refused by the server");
        } else if (held.length) {
          summary.held += 1;
          summary.reasons.push(...held.map(d => d?.reason).filter(Boolean));
        } else {
          summary.sent += 1;
        }
      } catch {
        summary.failed += 1;
      }
    }
    summary.reasons = [...new Set(summary.reasons)];
    return summary;
  }

  authHeaders() {
    let token = null;
    try {
      token = JSON.parse(localStorage.getItem("vibez-session") || "{}").token;
    } catch {
      token = null;
    }
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Reconcile one aggregate — serialized per aggregate. Every punch while
   * online lands here (meshService step 5), so two quick taps used to race two
   * whole-log POSTs; the slower one carried the STALER snapshot, arrived
   * second, and the server's diff faithfully restored the quantity the captain
   * had just taken back down. Now a flush already in flight gets exactly one
   * queued follow-up, which re-reads the log when it runs — so it carries
   * every event that arrived in the meantime, however many taps that was.
   *
   * Returns the server's disposition report (shared with every caller that
   * coalesced into the same flush), or throws if the request itself failed
   * (still offline, 5xx) — which is a retry, not a conflict, so nothing is
   * marked acknowledged.
   */
  reconcileAggregate(aggregateId) {
    const state = this._flushes.get(aggregateId) || {};
    this._flushes.set(aggregateId, state);
    if (state.running) {
      if (!state.queued) {
        state.queued = state.running.catch(() => {}).then(() => {
          state.queued = null;
          return this._startFlush(aggregateId, state);
        });
      }
      return state.queued;
    }
    return this._startFlush(aggregateId, state);
  }

  _startFlush(aggregateId, state) {
    state.running = this._reconcileNow(aggregateId).finally(() => {
      state.running = null;
      if (!state.queued) this._flushes.delete(aggregateId);
    });
    return state.running;
  }

  async _reconcileNow(aggregateId) {
    const events = await loadAggregateForSync(aggregateId);
    if (!events.length) return null;

    // A capture nobody managed to flush for half a day is an abandoned
    // experiment or a forgotten tablet — not tonight's table. Reconciling it
    // anyway punches ghost orders onto the live floor (seen 2026-08-20: stale
    // test captures flushed at a tunnel reconnect and "occupied" tables 3-5
    // with orders nobody placed). Quarantine instead; the 24h prune sweeps the
    // remains. Half a day is far beyond any sanctioned offline window — fiscal
    // billing is online-only, so real service cannot run offline that long.
    const newest = Math.max(0, ...events.map(e => Date.parse(e.created_at) || 0));
    if (newest < Date.now() - STALE_CAPTURE_MAX_AGE_MS) {
      console.warn(`[SyncService] ${aggregateId} last moved ${new Date(newest).toISOString()} — dropped as stale, not reconciled`);
      // Rejected takes it out of the flush queue; discarded keeps it off the
      // board. Rejected alone painted the table CONFLICT, and once pads linked
      // (2026-10-24) every pad received a colleague's day-old captures and
      // turned the whole floor red with them.
      await markAggregateRejected(aggregateId);
      await markAggregateDiscarded(aggregateId);
      return null;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${baseUrl}/api/mesh/reconcile`, {
      method: "POST",
      headers: this.authHeaders(),
      body: JSON.stringify({ aggregate_id: aggregateId, events }),
    });

    if (!res.ok) {
      throw new Error(`reconcile failed for ${aggregateId}: ${res.status}`);
    }

    const result = await res.json();

    if (result.conflict) {
      // The server said no for a business reason (the table belongs to another
      // order). Resending cannot change the answer, so the aggregate leaves the
      // flush queue and its projection — otherwise it re-argues every recovery
      // and keeps painting a table occupied that the server gave to someone else.
      await markAggregateRejected(aggregateId, result.conflict);
      this.conflicts = [
        ...this.conflicts.filter(c => c.aggregate_id !== aggregateId),
        { aggregate_id: aggregateId, ...result.conflict },
      ];
      if (this.onConflict) this.onConflict(this.conflicts);
      console.warn(`[SyncService] ${aggregateId} rejected: ${JSON.stringify(result.conflict)}`);
      return result;
    }

    const heldBack = [...new Set(
      [...(result.deferred || []), ...(result.skipped || [])].map(d => d?.reason).filter(Boolean)
    )];
    await saveOrderMapping(aggregateId, {
      order_id: result.order_id,
      order_number: result.order_number,
      order_status: result.order_status,
      held_back: heldBack,
    });
    await markEventsAcknowledged(result.acknowledged_event_ids);
    // Acknowledgement changed each event's _sync_state, which the reducer reads
    // for per-line KOT truth — re-run the fold so the screens see it.
    await rematerializeAggregate(aggregateId);

    if (result.deferred?.length) {
      // Most often a KOT the server would not issue. Deliberately NOT marked
      // acknowledged — the captain's screen must keep saying "Pending Server".
      console.warn(`[SyncService] ${aggregateId} deferred: ${JSON.stringify(result.deferred)}`);
    }
    if (result.skipped?.length) {
      console.warn(`[SyncService] ${aggregateId} skipped: ${JSON.stringify(result.skipped)}`);
    }

    return result;
  }

  /**
   * Re-send captures the server refused earlier, once per app start.
   *
   * Until 2026-10-23 the server refused a punch onto a busy table, or onto an
   * order that had moved, and the pad parked the capture as CONFLICTED: a red
   * table and a recovery dialog someone had to work through. The server now
   * joins the running order or follows the move, so most of those refusals
   * would succeed today. Each one goes back through the ordinary flush — the
   * server still decides, and a capture it refuses again is parked again with
   * the reason. A parked capture with nothing on it is simply released.
   */
  async retryRefusedCaptures() {
    const parked = (await offlineDb.mesh_order_map.toArray()).filter(
      m => m.order_status === "CONFLICTED"
    );
    for (const m of parked) {
      try {
        const mat = await offlineDb.captain_materialized.get(m.aggregate_id);
        const live = Object.values(mat?.state?.items || {}).filter(
          i => i.status !== "cancelled" && i.status !== "removed"
        );
        if (!live.length) {
          await markAggregateDiscarded(m.aggregate_id);
          continue;
        }
        // Only recent refusals. A capture refused hours ago may belong to a
        // party that has left; re-sending it would join whoever sits at that
        // table now. Those stay parked for a person to review.
        const events = await offlineDb.captain_events.where("aggregate_id").equals(m.aggregate_id).toArray();
        const newest = Math.max(0, ...events.map(e => Date.parse(e.created_at) || 0));
        // Half a day old: not tonight's table. Off the board, not left red.
        if (newest < Date.now() - STALE_CAPTURE_MAX_AGE_MS) {
          await markAggregateDiscarded(m.aggregate_id);
          continue;
        }
        if (newest < Date.now() - REFUSED_RETRY_MAX_AGE_MS) continue;
        await reviveRejectedAggregate(m.aggregate_id);
        await this.reconcileAggregate(m.aggregate_id);
      } catch (err) {
        // Back in the queue (revived) or still parked; the sweep carries on.
        console.warn(`[SyncService] retry of refused ${m.aggregate_id} failed`, err);
      }
    }
  }

  /**
   * Flush every aggregate this device still owes the server.
   * Guarded against re-entry: recovery can fire more than once.
   */
  async reconcileWithServer() {
    if (this.running) return;
    this.running = true;
    networkState.setMode(NetworkMode.RECONCILING);

    let failed = 0;
    try {
      const aggregateIds = await loadUnsyncedAggregateIds();
      console.log(`[SyncService] reconciling ${aggregateIds.length} aggregate(s)`);

      for (const aggregateId of aggregateIds) {
        try {
          await this.reconcileAggregate(aggregateId);
        } catch (err) {
          // One aggregate failing must not strand the others.
          failed += 1;
          console.error(`[SyncService] ${aggregateId} failed`, err);
        }
      }

      if (failed === aggregateIds.length && aggregateIds.length > 0) {
        // Nothing got through: the server is not really back.
        failoverMonitor.failoverToMesh();
        return;
      }

      networkState.setMode(NetworkMode.SERVER_CONNECTED);
    } catch (err) {
      console.error("[SyncService] reconciliation aborted", err);
      failoverMonitor.failoverToMesh();
    } finally {
      this.running = false;
    }
  }
}

export const syncService = new SyncService();
